import { useState, useRef, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useChatStore } from '../../stores/chatStore'
import { useUIStore } from '../../stores/uiStore'
import ChatMessage from './ChatMessage'
import NudgeChips from './NudgeChips'
import SupportTicketForm from './SupportTicketForm'

interface VersionInfo {
  version: string
  buildTime: string
  gitCommit: string
  goVersion: string
  serverTime: string
}

// Get the base API URL (without /api/v1 suffix)
const getBaseApiUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL || '/api/v1'
  // Remove /api/v1 suffix to get base URL
  return apiUrl.replace(/\/api\/v1$/, '')
}

const FAQ_RESPONSES: Record<string, string> = {
  'work order status': 'You can check your work order status by going to **Work Orders** in the sidebar. Each order shows its current status: Draft, Received, In Progress, Completed, or Delivered.',
  'payment methods': 'We accept multiple payment methods:\n- **Cash**\n- **UPI** (Google Pay, PhonePe, Paytm)\n- **Bank Transfer** (NEFT/RTGS/IMPS)\n- **Cheque**\n- **Credit/Debit Card**',
  'invoice download': 'To download an invoice:\n1. Go to **Invoices** in the sidebar\n2. Click on the invoice you want\n3. Click the **Download PDF** button in the top right',
  'turnaround time': 'Standard turnaround times:\n- **Single Crown**: 3-4 working days\n- **Bridge (3 units)**: 4-5 working days\n- **Full Denture**: 5-7 working days\n- **Implant Crown**: 4-5 working days\n\nUrgent orders can be expedited for an additional fee.',
  'gst invoice': 'All our invoices are GST compliant. We charge:\n- **CGST + SGST** (9% each) for Karnataka deliveries\n- **IGST** (18%) for other states\n\nYour GST number will appear on all invoices if provided.',
  'contact support': 'You can reach us via:\n- **Phone**: +91 98765 43210\n- **Email**: support@jasminedental.com\n- **WhatsApp**: +91 98765 43210\n\nOr create a support ticket below and we\'ll get back to you within 24 hours.',
}

// Store for cached version to detect if we're getting stale responses
let cachedVersion: VersionInfo | null = null

export default function ChatWindow() {
  const { messages, addMessage, isTyping, setIsTyping } = useChatStore()
  const { toggleChat } = useUIStore()
  const [input, setInput] = useState('')
  const [showTicketForm, setShowTicketForm] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Fetch version from API with cache-busting
  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      const baseUrl = getBaseApiUrl()
      // Add timestamp to bust any CDN/browser cache
      const response = await axios.get<VersionInfo>(`${baseUrl}/version?_t=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
      return response.data
    } catch {
      return null
    }
  }, [])

  // Format version info for display
  const formatVersionResponse = (version: VersionInfo, isCached: boolean): string => {
    const buildDate = new Date(version.buildTime).toLocaleString()
    const serverDate = new Date(version.serverTime).toLocaleString()
    
    let response = `**App Version Information**\n\n`
    response += `- **Version**: ${version.version}\n`
    response += `- **Build Time**: ${buildDate}\n`
    response += `- **Git Commit**: ${version.gitCommit}\n`
    response += `- **Go Version**: ${version.goVersion}\n`
    response += `- **Server Time**: ${serverDate}\n`
    
    if (isCached) {
      response += `\n⚠️ **Cache Warning**: You may be seeing a cached response. `
      response += `If you just deployed, try hard-refreshing the page (Ctrl+Shift+R or Cmd+Shift+R) `
      response += `or clearing your browser cache.`
    } else if (cachedVersion && cachedVersion.version !== version.version) {
      response += `\n✅ **New Version Detected**: Updated from v${cachedVersion.version} to v${version.version}!`
    }
    
    return response
  }

  const handleSend = async () => {
    if (!input.trim()) return

    const userMessage = input.trim()
    setInput('')

    addMessage({
      role: 'user',
      content: userMessage,
    })

    setIsTyping(true)

    // Simple keyword matching for FAQ
    const lowerInput = userMessage.toLowerCase()
    let botResponse = ''

    // Check for version-related queries
    const versionKeywords = ['version', 'build', 'deployment', 'deployed', 'what version', 'which version', 'app version', 'current version']
    const isVersionQuery = versionKeywords.some(kw => lowerInput.includes(kw))

    if (isVersionQuery) {
      const versionInfo = await fetchVersion()
      
      if (versionInfo) {
        // Check if this might be a cached response
        const serverTime = new Date(versionInfo.serverTime)
        const now = new Date()
        const timeDiff = Math.abs(now.getTime() - serverTime.getTime())
        const isCached = timeDiff > 60000 // More than 1 minute difference suggests caching
        
        botResponse = formatVersionResponse(versionInfo, isCached)
        
        // Update cached version for comparison
        cachedVersion = versionInfo
      } else {
        botResponse = "Sorry, I couldn't fetch the version information. The server might be temporarily unavailable. Please try again in a moment."
      }
    } else {
      // Simulate bot thinking for non-version queries
      await new Promise(resolve => setTimeout(resolve, 800))

      for (const [keyword, response] of Object.entries(FAQ_RESPONSES)) {
        if (lowerInput.includes(keyword.split(' ')[0]) || lowerInput.includes(keyword)) {
          botResponse = response
          break
        }
      }

      if (!botResponse) {
        if (lowerInput.includes('help') || lowerInput.includes('support')) {
          botResponse = "I'm here to help! You can ask me about:\n- Work order status\n- Payment methods\n- Invoice downloads\n- Turnaround times\n- GST invoices\n- **App version** (current deployment)\n\nOr click **Create Ticket** below to speak with our support team."
        } else if (lowerInput.includes('thank')) {
          botResponse = "You're welcome! Is there anything else I can help you with?"
        } else if (lowerInput.includes('hi') || lowerInput.includes('hello')) {
          botResponse = "Hello! 👋 I'm your Jasmine Dental Lab assistant. How can I help you today?"
        } else {
          botResponse = "I'm not sure I understand. Could you try rephrasing that? Or you can click one of the quick options below, or **Create Ticket** to speak with our support team."
        }
      }
    }

    setIsTyping(false)
    addMessage({
      role: 'bot',
      content: botResponse,
    })
  }

  const handleNudgeClick = (nudge: string) => {
    setInput(nudge)
    // Auto-send after setting
    setTimeout(() => {
      addMessage({ role: 'user', content: nudge })
      setIsTyping(true)

      setTimeout(() => {
        const response = FAQ_RESPONSES[nudge.toLowerCase()] || "Let me find that information for you..."
        setIsTyping(false)
        addMessage({ role: 'bot', content: response })
      }, 800)
    }, 100)
    setInput('')
  }

  return (
    <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col z-50 animate-slide-up overflow-hidden">
      {/* Header */}
      <div className="bg-primary-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="fas fa-tooth text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold">Jasmine Support</h3>
            <p className="text-xs text-white/80">We typically reply instantly</p>
          </div>
        </div>
        <button
          onClick={toggleChat}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Close chat"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-80 min-h-[200px]">
        {messages.length === 0 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-comments text-2xl text-primary-600"></i>
            </div>
            <p className="text-gray-600 dark:text-gray-300 font-medium">Welcome to Jasmine Support!</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Ask a question or select a topic below
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        {isTyping && (
          <div className="flex items-center gap-2 text-gray-500">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
            <span className="text-sm">Typing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Nudge Chips - Always show after responses */}
      {!showTicketForm && !isTyping && (
        <NudgeChips onNudgeClick={handleNudgeClick} />
      )}

      {/* Support Ticket Form */}
      {showTicketForm && (
        <SupportTicketForm onClose={() => setShowTicketForm(false)} />
      )}

      {/* Input */}
      {!showTicketForm && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="w-10 h-10 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
          <button
            onClick={() => setShowTicketForm(true)}
            className="w-full mt-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            <i className="fas fa-ticket-alt mr-1"></i>
            Create Support Ticket
          </button>
        </div>
      )}
    </div>
  )
}
